<template>
  <BDXEditFormWrapper v-bind="$attrs" :preview-item="previewItem">
    <ValidationObserver v-slot="{ handleSubmit }" slim>
      <form @submit.prevent="handleSubmit(onSubmit)">
        <ErrorMessages :errors="errors" />

        <b-row>
          <b-col lg="4">
            <BDXEditFormFCPStatus v-model="formData.fcp_status" />
          </b-col>
          <b-col lg="4">
            <BDXEditFormFCPStartDate
              v-if="formData.fcp_status"
              v-model="formData.fcp_start_date"
              :required="formData.fcp_status"
            />
          </b-col>
          <b-col lg="4" v-if="formData.jurisdiction">
            <BaseInput
              v-model="formData.jurisdiction"
              size="sm"
              readonly
              label="Jurisdiction"
              class="jurisdiction-select"
              tabindex="-1"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="4">
            <BDXEditFormContinuousContract
              v-model="formData.continuous_contract"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <RLSUnderwriterLeadsChecklist
              v-model="formData.underwriter_lead"
              :disabled="isLeadUnderwriterDisabled"
              :searchable="false"
              :is-fetching="isFetchingUWRLSGroupItems"
              :rls-group-items="rlsUWGroupItems"
              :auto-fetch-rls-group-items="false"
            />
          </b-col>
          <b-col>
            <RLSUnderwriterFollowersChecklist
              v-model="formData.underwriter_follower"
              :disabled="isFollowUnderwriterAssignmentDisabled"
              :searchable="false"
              :is-fetching="isFetchingUWRLSGroupItems"
              :rls-group-items="rlsUWGroupItems"
              :auto-fetch-rls-group-items="false"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col v-if="!isCompanyRoleName('TPA')">
            <BDXEditFormTPA
              :disabled="isTpaDisabled"
              v-model="formData.tpa"
              @created:tpa="(newItem) => onRLSItemCreated(newItem, 'tpa')"
              @removed:tpa="() => onRLSItemRemoved('tpa')"
            />
          </b-col>
          <b-col v-if="!isCompanyRoleName('Broker')">
            <BDXEditFormBroker
              :disabled="isBrokerDisabled"
              v-model="formData.broker"
              @created:broker="(newItem) => onRLSItemCreated(newItem, 'broker')"
              @removed:broker="() => onRLSItemRemoved('broker')"
            />
          </b-col>
          <b-col v-if="!isCompanyRoleName('Coverholder')">
            <BDXEditFormCoverholder
              :disabled="isCoverholderDisabled"
              v-model="formData.coverholder"
              @created:coverholder="
                (newItem) => onRLSItemCreated(newItem, 'coverholder')
              "
              @removed:coverholder="() => onRLSItemRemoved('coverholder')"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="4">
            <BDXEditFormCurrency v-model="formData.currency_type" />
          </b-col>
          <b-col lg="4">
            <BDXEditFormYearOfAccount
              v-model="formData.year_of_account"
              name="Year of account"
              class="w-100 mt-auto"
              :required="formData.continuous_contract"
            />
          </b-col>
          <b-col lg="4">
            <BaseInput
              v-model="formData.ucr"
              :disabled="isMultiIds"
              label="UCR"
              size="sm"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <BDXEditFormAccountType
              v-model="formData.account_type"
              :disabled="isMultiIds"
              :required="formData.fcp_status"
            />
          </b-col>
          <b-col>
            <BDXEditFormAccountName
              v-model="formData.account_name"
              :umr="umr"
              :disabled="isMultiIds"
              :required="formData.fcp_status"
            />
          </b-col>
          <b-col>
            <BaseInput
              v-model="formData.account_code"
              :disabled="isMultiIds"
              label="Account Code"
              size="sm"
            />
          </b-col>
        </b-row>

        <div class="mt-2">
          <BaseButton
            :loading="isSubmitting"
            :disabled="isSlipShareExeeded || isSubmitting"
            type="submit"
            >Save</BaseButton
          >
        </div>
      </form>
    </ValidationObserver>
  </BDXEditFormWrapper>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  provide,
  onMounted,
  watch
} from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import { createVuexHelpers } from 'vue2-helpers'
import { startsWith } from 'lodash-es'

import { BDX_CURRENCY_TYPES, BDX_ACCOUNT_TYPES } from '@constants'
import {
  useToast,
  useUserCompany,
  useCurrentUser,
  useRLSGroup
} from '@composables'
import { LincolnGlobalUMRService, BillingManagementService } from '@services'

import { BaseInput, BaseButton } from '@components/base'
import { ErrorMessages } from '@components/misc'
import {
  RLSUnderwriterLeadsChecklist,
  RLSUnderwriterFollowersChecklist
} from '@components/rls'

import { BDXEditFormInjectionKey } from './BDXEditForm.constants'
import BDXEditFormWrapper from './BDXEditFormWrapper.vue'
import BDXEditFormYearOfAccount from './BDXEditFormYearOfAccount.vue'
import BDXEditFormTPA from './BDXEditFormTPA.vue'
import BDXEditFormBroker from './BDXEditFormBroker.vue'
import BDXEditFormCoverholder from './BDXEditFormCoverholder.vue'
import BDXEditFormFCPStatus from './BDXEditFormFCPStatus.vue'
import BDXEditFormFCPStartDate from './BDXEditFormFCPStartDate.vue'
import BDXEditFormCurrency from './BDXEditFormCurrency.vue'
import BDXEditFormAccountType from './BDXEditFormAccountType.vue'
import BDXEditFormAccountName, { clearAccountNameCache } from './BDXEditFormAccountName.vue'
import BDXEditFormContinuousContract from './BDXEditFormContinuousContract.vue'

export default defineComponent({
  name: 'BDXEditForm',
  components: {
    ValidationObserver,

    BaseInput,
    BaseButton,

    ErrorMessages,

    RLSUnderwriterLeadsChecklist,
    RLSUnderwriterFollowersChecklist,

    BDXEditFormWrapper,
    BDXEditFormYearOfAccount,
    BDXEditFormTPA,
    BDXEditFormBroker,
    BDXEditFormCoverholder,
    BDXEditFormFCPStatus,
    BDXEditFormFCPStartDate,
    BDXEditFormCurrency,
    BDXEditFormAccountType,
    BDXEditFormAccountName,
    BDXEditFormContinuousContract
  },
  props: {
    value: Boolean,
    ids: {
      type: Array,
      default: () => []
    },
    leadUnderwriters: {
      type: Array,
      default: () => []
    },
    followerUnderwriters: {
      type: Array,
      default: () => []
    },
    tpa: {
      type: Array,
      default: () => []
    },
    broker: {
      type: Array,
      default: () => []
    },
    coverholder: {
      type: Array,
      default: () => []
    },
    ucr: String,
    continuousContract: Boolean,
    fcpStatus: Boolean,
    fcpStartDate: String,
    currencyType: {
      type: String,
      validator: (checkValue) => BDX_CURRENCY_TYPES.includes(checkValue)
    },
    yearOfAccount: Number,
    accountType: {
      type: String,
      validator: (checkValue) => BDX_ACCOUNT_TYPES.includes(checkValue)
    },
    accountName: String,
    // Key's UMR - scopes the Account Name dropdown (LIQ-2410)
    umr: String,
    accountCode: String,
    jurisdiction: String,
    isModal: Boolean,
    previewItem: { type: Object, default: null }
  },
  emits: ['success', 'change'],
  setup(props, { emit }) {
    const { useGetters } = createVuexHelpers()

    const { userCompanyRLS } = useGetters(['userCompanyRLS'])

    const {
      isFetching: isFetchingUWRLSGroupItems,
      rlsGroupItems: rlsUWGroupItems,
      fetchRLSGroupItems
    } = useRLSGroup('Underwriter', false)

    // Own copy of the TPA rls-group list, fetched purely to resolve the
    // selected TPA's LimeIQ company_id for billing provisioning below -
    // BDXEditFormTPA.vue fetches/renders its own dropdown independently.
    const { rlsGroupItems: rlsTPAGroupItems, fetchRLSGroupItems: fetchTPARLSGroupItems } =
      useRLSGroup('TPA', false)

    const { showToast } = useToast()
    const { isRoleName: isCompanyRoleName } = useUserCompany()
    const { userEmail, isSuperUser } = useCurrentUser()

    // Modal
    const showModal = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const hasLeadUnderwriter = computed(
      () => !!formData.value.underwriter_lead && formData.value.underwriter_lead.length > 0
    )

    // A Follow Underwriter on this record cannot assign any party on it -
    // TPA, Broker, Coverholder, or Lead/Follow Underwriter - only the
    // current Lead Underwriter (or a superuser) can (LIQ-2028). Before a
    // lead is even set, there's no "follow" yet, so nothing to block here.
    const isFollowUnderwriterAssignmentDisabled = computed(() => {
      if (isSuperUser.value) return false
      if (!isCompanyRoleName('Underwriter')) return false
      if (!hasLeadUnderwriter.value) return false

      return !formData.value.underwriter_lead.some(
        (leadUnderwriter) => leadUnderwriter.id === userCompanyRLS.value.id
      )
    })

    // Once a Lead Underwriter is set on a record, an Underwriter user
    // (lead or follow) can no longer reassign it - only a superuser can.
    // With no lead set yet, an Underwriter can still assign one. TPA,
    // Broker and Coverholder users are never gated by this - they can
    // always change the Lead Underwriter field, set or not.
    const isLeadUnderwriterDisabled = computed(() => {
      if (isSuperUser.value) return false
      if (!isCompanyRoleName('Underwriter')) return false

      return hasLeadUnderwriter.value
    })

    const isTpaDisabled = computed(() => {
      if (isFollowUnderwriterAssignmentDisabled.value) return true

      const hasTpa = !!props.tpa && props.tpa.length > 0
      if (!hasTpa) return false

      return !isSuperUser.value;
    })

    const isBrokerDisabled = computed(() => isFollowUnderwriterAssignmentDisabled.value)

    const isCoverholderDisabled = computed(() => {
      if (isFollowUnderwriterAssignmentDisabled.value) return true

      const hasCoverholder = !!props.coverholder && props.coverholder.length > 0
      if (!hasCoverholder) return false

      return !isSuperUser.value;
    })

    const handleHideModal = () => {
      emit('input', false)
    }

    // General
    const isMultiIds = computed(() => 1 < props.ids.length)

    const errors = ref([])
    const hasChanges = ref(false)

    const formData = ref({
      tpa: [],
      broker: [],
      coverholder: [],
      fcp_start_date: '',
      underwriter_lead: [],
      underwriter_follower: [],
      ucr: '',
      continuous_contract: false,
      fcp_status: false,
      fcp_start_date: '',
      currency_type: '',
      account_name: '',
      account_code: '',
      account_type: '',
      year_of_account: '',
      new_tpa: '',
      new_broker: [],
      new_coverholder: [],
      jurisdiction: ''
    })

    // Sums every syndicate line's share across both buckets, same sanity
    // check the backend's own serializer does before it even starts
    // writing - see GlobalUMRUpdateSerializer.validate's identical sum. An
    // underwriter holding two rows (lead and follow) contributes both of
    // its lines here, same as any other underwriter's two syndicate lines.
    const isSlipShareExeeded = computed(() => {
      const leadUnderwriters = formData.value.underwriter_lead
      const followerUnderwriters = formData.value.underwriter_follower
      const totalPct = [...leadUnderwriters, ...followerUnderwriters].reduce(
        (sum, { syndicates = [] }) => {
          return (
            sum +
            syndicates.reduce((lineSum, { slip_share_pct }) => {
              const pct = parseFloat(slip_share_pct)
              return lineSum + (Number.isFinite(pct) ? pct : 0)
            }, 0)
          )
        },
        0
      )
      return totalPct > 100
    })

    watch(isSlipShareExeeded, (isExceeded) => {
      const label = 'Slip Share Exceeded'
      if (isExceeded) {
        errors.value.push({
          label: label,
          message:
            'Lead Underwriter and Follower Underwriters total slip share exceeds 100%.'
        })
      } else {
        errors.value = errors.value.filter(
          ({ label: errorLabel }) => errorLabel !== label
        )
      }
    })

    const initFormData = () => {
      formData.value.globalumr_ids = props.ids
      formData.value.underwriter_lead = props.leadUnderwriters
      formData.value.underwriter_follower = props.followerUnderwriters
      formData.value.tpa = props.tpa
      formData.value.broker = props.broker
      formData.value.coverholder = props.coverholder
      formData.value.ucr = props.ucr
      formData.value.continuous_contract = props.continuousContract
      formData.value.fcp_status = props.fcpStatus
      formData.value.fcp_start_date = props.fcpStartDate || ''
      formData.value.currency_type = props.currencyType
      formData.value.year_of_account = props.yearOfAccount
      formData.value.account_name = props.accountName
      formData.value.account_code = props.accountCode
      formData.value.account_type = props.accountType
      formData.value.jurisdiction = props.jurisdiction || ''

      const { id: rlsID } = userCompanyRLS.value

      if (isCompanyRoleName('TPA')) {
        formData.value.tpa = [rlsID]
      } else if (isCompanyRoleName('Broker')) {
        formData.value.broker = [rlsID]
      } else if (isCompanyRoleName('Coverholder')) {
        formData.value.coverholder = [rlsID]
      }

      //Init underwriters fetch for both checklist components to share.
      fetchRLSGroupItems()

      // Own TPA fetch, used only to resolve company_id for billing (see below).
      fetchTPARLSGroupItems()

      // Initialize watcher after the first update (only watch subsequent updates)
      watch(formData, (_) => (hasChanges.value = true), { deep: true })
    }

    const filterRLSGroup = (rlsGroup, filterNewItems = true) => {
      return rlsGroup
        .filter((item) => {
          if (!item) return false

          if (filterNewItems) {
            return startsWith(item, 'new:') ? false : true
          } else {
            return startsWith(item, 'new:')
          }
        })
        .map((item) => {
          if (filterNewItems) return item

          const chars = item.split(':')

          return chars[2]
        })
    }

    const buildFormData = (patchData = {}) => {
      const rlsTPAs = filterRLSGroup(formData.value.tpa || [])
      const rlsBrokers = filterRLSGroup(formData.value.broker || [])
      const rlsCoverholders = filterRLSGroup(formData.value.coverholder || [])
      const rlsUnderwriterLeads = (
        formData.value.underwriter_lead || []
      ).filter((item) => !!item)
      const rlsUnderwriterFollowers = (
        formData.value.underwriter_follower || []
      ).filter((item) => !!item)

      const newRLSTPAs = filterRLSGroup(formData.value.tpa || [], false)
      const newRLSBrokers = filterRLSGroup(formData.value.broker || [], false)
      const newRLSCoverholders = filterRLSGroup(
        formData.value.coverholder || [],
        false
      )

      return {
        globalumr_ids: formData.value.globalumr_ids,
        rls_tpa: !!rlsTPAs.length ? rlsTPAs : undefined,
        rls_broker: !!rlsBrokers.length ? rlsBrokers : undefined,
        rls_coverholder: !!rlsCoverholders.length ? rlsCoverholders : undefined,
        underwriter_lead: !!rlsUnderwriterLeads.length
          ? rlsUnderwriterLeads
          : undefined,
        underwriter_follower: !!rlsUnderwriterFollowers.length
          ? rlsUnderwriterFollowers
          : undefined,
        fcp_status: formData.value.fcp_status,
        // Per explicit decision, no longer gated on any associated company having rec_enabled=1
        // (removed the hasQualifyingCompany check that used to sit here) - this form's only real
        // usage (BDXMatchPendingManager.vue) always creates a brand new key, so REC is simply on
        // unconditionally for every key created through it.
        rec_status: true,
        currency_type: formData.value.currency_type,
        ucr: formData.value.ucr,
        account_type: formData.value.account_type || null,
        account_name: formData.value.account_name,
        jurisdiction: formData.value.jurisdiction,
        account_code: formData.value.account_code,
        year_of_account: formData.value.year_of_account,
        continuous_contract: formData.value.continuous_contract,
        ...(formData.value.fcp_status && {
          fcp_start_date: formData.value.fcp_start_date
        }),
        new_tpa: !!newRLSTPAs.length ? newRLSTPAs[0] : '',
        new_broker: newRLSBrokers,
        new_coverholder: newRLSCoverholders,
        user_email: userEmail.value,
        ...patchData
      }
    }

    const findCompanyId = (rlsGroupItemsList, rlsId) => {
      const match = (rlsGroupItemsList.value || []).find(
        (rlsGroupItem) => rlsGroupItem.id === rlsId
      )
      return match ? match.company_id : null
    }

    // Only the Lead Underwriter now on the record, paired with the
    // record's TPA, resolved to LimeIQ company ids so a CompanyBilling row
    // can be provisioned for that relationship - Follower Underwriters
    // don't trigger billing provisioning.
    const buildBillingEnsurePairs = () => {
      const rlsTPAs = filterRLSGroup(formData.value.tpa || [])
      const leadUnderwriterRlsIds = [
        ...new Set(
          (formData.value.underwriter_lead || [])
            .filter((item) => !!item)
            .map((item) => item.id)
        )
      ]

      const pairs = []
      rlsTPAs.forEach((tpaRlsId) => {
        const tpaCompanyId = findCompanyId(rlsTPAGroupItems, tpaRlsId)
        if (!tpaCompanyId) return

        leadUnderwriterRlsIds.forEach((underwriterRlsId) => {
          const underwriterCompanyId = findCompanyId(rlsUWGroupItems, underwriterRlsId)
          if (!underwriterCompanyId) return

          pairs.push({ underwriterCompanyId, tpaCompanyId })
        })
      })
      return pairs
    }

    const ensureBillingForCurrentAssignments = async () => {
      const pairs = buildBillingEnsurePairs()

      await Promise.all(
        pairs.map(({ underwriterCompanyId, tpaCompanyId }) =>
          BillingManagementService.ensureBilling(underwriterCompanyId, tpaCompanyId).catch((e) => {
            // Provisioning billing config is a side effect of this save, not
            // a requirement for it - the BDX edit has already succeeded.
            console.error('Failed to ensure billing row for BDX assignment', e)
          })
        )
      )
    }

    // New RLS Item
    const onRLSItemCreated = (newItem, key = 'tpa') => {
      if (!newItem) return

      formData.value[key].push(newItem.id)
    }

    const onRLSItemRemoved = (key = 'tpa') => {
      if ('tpa' === key) {
        formData.value[`new_${key}`] = ''
      } else {
        formData.value[`new_${key}`] = []
      }
    }

    // Form handling
    const isSubmitting = ref(false)

    const onSubmit = async (patchData = {}) => {
      errors.value = []

      isSubmitting.value = true

      try {
        const builtFormData = buildFormData(patchData)

        await LincolnGlobalUMRService.update(builtFormData, {
          transformResponse: [
            (res) =>
              JSON.stringify({
                ...JSON.parse(res),
                noToastError: true
              })
          ]
        })

        ensureBillingForCurrentAssignments()

        isSubmitting.value = false

        hasChanges.value = false

        // A newly typed Account Name is now on the key; refetch so it shows in later dropdowns.
        clearAccountNameCache(props.umr)

        emit('success')
        emit('input', false)

        Vue.prototype.$emitter.emit('bdx-edit-form:success', {
          status: true
        })

        showToast('Saved successfully.')
      } catch (e) {
        isSubmitting.value = false

        let { data } = e.response || {}
        data = JSON.parse(data) || {}

        if (!!data) {
          if (data.duplicate_globalumr) {
            const duplicateGlobalumrError = data.duplicate_globalumr
            if (
              duplicateGlobalumrError !== undefined &&
              duplicateGlobalumrError.length > 0
            ) {
              Vue.prototype.$swal({
                title: 'Confirmation',
                text: duplicateGlobalumrError[0],
                type: 'warning',
                confirmButtonText: 'Okay',
                preConfirm: async (isConfirmed) => {
                  if (!isConfirmed) return
                  onSubmit({ prompt_merge: true })
                }
              })
            }
          } else if (data.message) {
            console.log(data.message)
            console.log(typeof data.message, data.message)
            showToast(data.message, 'error')
          }else {
            const { data: error } = data
            if (!!error) {
              const newErrors = Object.entries(error[0]).map(([key, info]) => {
                const label = key.replace(/_/g, ' ')
                const message = info[0] || 'This field is required'
                return {
                  label,
                  message
                }
              })
              errors.value = newErrors
            }
          }
        }
      }
    }

    onMounted(() => {
      if (props.isModal) return

      initFormData()
    })

    watch(showModal, (newShowModal) => {
      if (newShowModal) {
        initFormData()
      } else {
        hasChanges.value = false
      }
    })

    provide(BDXEditFormInjectionKey, {
      isModal: props.isModal,
      showModal,
      handleHideModal,

      hasChanges
    })

    return {
      isCompanyRoleName,

      isMultiIds,

      errors,

      formData,

      onRLSItemCreated,
      onRLSItemRemoved,

      isTpaDisabled,
      isBrokerDisabled,
      isCoverholderDisabled,
      isFollowUnderwriterAssignmentDisabled,
      isLeadUnderwriterDisabled,

      isSubmitting,
      onSubmit,
      isSlipShareExeeded,
      isFetchingUWRLSGroupItems,
      rlsUWGroupItems
    }
  }
})
</script>

<style scoped>
form {
  display: grid;
  row-gap: 12px;
}

form >>> label,
form >>> .col-form-label {
  line-height: 1.2;
}

form .mx-datepicker {
  margin-top: 7px;
}
.jurisdiction-select input {
  pointer-events: none;
  background-color: #f5f5f5;
}
</style>
